import { DetailsTileTitle } from "@/components/license-details/tile-info";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type LicenseNotificationEmailDetail,
  type LicenseNotificationEmailListItem,
  getLicenseNotificationEmail,
  listLicenseNotificationEmails,
  notificationEmailKindLabel,
} from "@/hera-api/notification-emails-api";
import { formatDateTime } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

export function LicenseNotificationEmailsCard({
  licenseId,
}: {
  licenseId: string;
}) {
  const [viewingId, setViewingId] = useState<string | null>(null);
  const listQuery = useQuery({
    queryKey: ["listLicenseNotificationEmails", licenseId],
    queryFn: () => listLicenseNotificationEmails(licenseId),
    enabled: Boolean(licenseId),
  });
  const detailQuery = useQuery({
    queryKey: ["getLicenseNotificationEmail", licenseId, viewingId],
    queryFn: () => getLicenseNotificationEmail(licenseId, viewingId ?? ""),
    enabled: Boolean(licenseId && viewingId),
  });

  const items = listQuery.data?.items ?? [];

  return (
    <Card className="gap-4">
      <CardHeader>
        <DetailsTileTitle
          name="Correspondence"
          infoBody="Emails sent about this registration, including comments shared with the registrant."
        />
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {listQuery.isLoading ? (
          <p className="text-muted-foreground">Loading correspondence…</p>
        ) : listQuery.isError ? (
          <p className="text-destructive" role="alert">
            {listQuery.error instanceof Error
              ? listQuery.error.message
              : "Failed to load correspondence"}
          </p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground">
            No correspondence has been sent for this registration.
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => (
              <NotificationEmailRow
                key={item.id}
                item={item}
                onView={() => setViewingId(item.id)}
              />
            ))}
          </ul>
        )}
      </CardContent>
      <NotificationEmailViewDialog
        open={viewingId !== null}
        onOpenChange={(open) => {
          if (!open) setViewingId(null);
        }}
        detail={detailQuery.data ?? null}
        isLoading={detailQuery.isLoading}
        error={
          detailQuery.isError
            ? detailQuery.error instanceof Error
              ? detailQuery.error.message
              : "Failed to load email"
            : null
        }
      />
    </Card>
  );
}

function NotificationEmailRow({
  item,
  onView,
}: {
  item: LicenseNotificationEmailListItem;
  onView: () => void;
}) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-md border p-2">
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium leading-snug">{item.subject}</p>
        <p className="text-muted-foreground">
          {notificationEmailKindLabel(item.kind)} · {item.to_email}
        </p>
        <p className="text-muted-foreground">{formatDateTime(item.sent_at)}</p>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onView}>
        View
      </Button>
    </li>
  );
}

function NotificationEmailViewDialog({
  open,
  onOpenChange,
  detail,
  isLoading,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: LicenseNotificationEmailDetail | null;
  isLoading: boolean;
  error: string | null;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{detail?.subject ?? "Correspondence"}</DialogTitle>
          <DialogDescription>
            {detail
              ? `Sent to ${detail.to_email} on ${formatDateTime(detail.sent_at)}`
              : "Stored copy of the email as sent."}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading email…</p>
        ) : error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : detail ? (
          <Tabs defaultValue="html">
            <TabsList>
              <TabsTrigger value="html">HTML</TabsTrigger>
              <TabsTrigger value="text">Plain text</TabsTrigger>
            </TabsList>
            <TabsContent value="html">
              <iframe
                title="Correspondence HTML"
                sandbox=""
                srcDoc={detail.html_body}
                className="h-[60vh] w-full rounded-md border bg-white"
              />
            </TabsContent>
            <TabsContent value="text">
              <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm">
                {detail.text_body}
              </pre>
            </TabsContent>
          </Tabs>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
